import { useState, useRef, useEffect } from 'react'
import { changeLanguage } from '@/shared/lib/i18n'
import { useI18n } from '@/shared/hooks/useI18n'
import { I18nSupportedLanguage } from '../types/language-select'

interface UseLanguageSelectProps {
  initialValue?: string
  onLanguageChange?: (language: string) => void
}

export const useLanguageSelect = ({
  initialValue = 'ko',
  onLanguageChange
}: UseLanguageSelectProps = {}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState(initialValue)
  const selectRef = useRef<HTMLDivElement>(null)
  const { currentLanguage: i18nCurrentLanguage } = useI18n()

  // i18n 언어 변경 시 selectedLanguage 동기화
  useEffect(() => {
    if (i18nCurrentLanguage && i18nCurrentLanguage !== selectedLanguage) {
      setSelectedLanguage(i18nCurrentLanguage)
    }
  }, [i18nCurrentLanguage, selectedLanguage])

  // initialValue 변경 시 selectedLanguage 업데이트
  useEffect(() => {
    if (initialValue && initialValue !== selectedLanguage) {
      setSelectedLanguage(initialValue)
    }
  }, [initialValue, selectedLanguage])

  // 외부 클릭 감지하여 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // 드롭다운 토글
  const toggleDropdown = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsOpen(prev => !prev)
  }

  // 언어 선택 처리
  const selectLanguage = async (languageValue: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    setSelectedLanguage(languageValue)

    try {
      // i18n에서 지원하는 언어만 changeLanguage 호출
      if (languageValue === 'ko' || languageValue === 'en' || languageValue === 'zh' || languageValue === 'ja') {
        await changeLanguage(languageValue as I18nSupportedLanguage)
      }
      onLanguageChange?.(languageValue)
    } catch (error) {
      console.error('Language change failed:', error)
    }

    setIsOpen(false)
  }

  return {
    isOpen,
    selectedLanguage,
    selectRef,
    toggleDropdown,
    selectLanguage,
    setSelectedLanguage
  }
}
